import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { 
  MessageSquare, 
  Send, 
  Headphones,
  Package,
  CreditCard,
  HelpCircle,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const SupportContact = ({ 
  orderId = null, 
  listingId = null, 
  triggerButton = null,
  onTicketCreated = null 
}) => {
  const { user, token, API } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTickets, setShowTickets] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: orderId ? 'order' : listingId ? 'listing' : '',
    reference_id: orderId || listingId || '',
    message: ''
  });

  useEffect(() => {
    if (user && token && open) {
      fetchTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, open]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/support/tickets`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Votre demande a été envoyée');
      setFormData({ subject: '', reference_id: '', message: '' });
      fetchTickets();
      if (onTicketCreated) onTicketCreated(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (ticketId, message) => {
    if (!message.trim()) return;
    
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, 
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réponse envoyée');
      fetchTickets();
      // Refresh selected ticket
      const response = await axios.get(`${API}/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const getSubjectIcon = (subject) => {
    switch (subject) {
      case 'order': return <Package className="h-4 w-4" />;
      case 'listing': return <Package className="h-4 w-4" />;
      case 'payment': return <CreditCard className="h-4 w-4" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  const getSubjectLabel = (subject) => {
    switch (subject) {
      case 'order': return 'Commande';
      case 'listing': return 'Annonce';
      case 'payment': return 'Paiement';
      default: return 'Autre';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-yellow-500/20 text-yellow-500">En attente</Badge>;
      case 'in_progress':
        return <Badge className="bg-primary/20 text-primary">En cours</Badge>;
      case 'closed':
        return <Badge className="bg-green-500/20 text-green-500">Résolu</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) return null;

  const trigger = triggerButton || (
    <Button variant="outline" className="gap-2" data-testid="support-contact-btn">
      <Headphones className="h-4 w-4" />
      Contacter le support
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headphones className="h-5 w-5 text-primary" />
            Support Kacos
          </DialogTitle>
          <DialogDescription>
            Envoyez un message à notre équipe. Nous vous répondrons dans les plus brefs délais.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <Button 
            variant={!showTickets ? 'default' : 'outline'} 
            size="sm"
            onClick={() => { setShowTickets(false); setSelectedTicket(null); }}
          >
            Nouveau message
          </Button>
          <Button 
            variant={showTickets ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setShowTickets(true)}
          >
            Mes demandes ({tickets.length})
          </Button>
        </div>

        {!showTickets ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Sujet *</Label>
              <Select
                value={formData.subject}
                onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
              >
                <SelectTrigger data-testid="support-subject-select">
                  <SelectValue placeholder="Sélectionnez un sujet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="order">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Question sur une commande
                    </div>
                  </SelectItem>
                  <SelectItem value="listing">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Question sur une annonce
                    </div>
                  </SelectItem>
                  <SelectItem value="payment">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Question sur un paiement
                    </div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Autre question
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formData.subject === 'order' || formData.subject === 'listing') && (
              <div className="space-y-2">
                <Label htmlFor="reference_id">
                  ID de {formData.subject === 'order' ? 'la commande' : "l'annonce"} (optionnel)
                </Label>
                <Input
                  id="reference_id"
                  value={formData.reference_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_id: e.target.value }))}
                  placeholder="Ex: abc123..."
                  data-testid="support-reference-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="message">Votre message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Décrivez votre problème ou votre question..."
                rows={5}
                required
                data-testid="support-message-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full gap-2" 
              disabled={loading}
              data-testid="support-submit-btn"
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Envoyer
            </Button>
          </form>
        ) : selectedTicket ? (
          <TicketDetail 
            ticket={selectedTicket} 
            onReply={handleReply}
            onBack={() => setSelectedTicket(null)}
            formatDate={formatDate}
            getSubjectLabel={getSubjectLabel}
            getStatusBadge={getStatusBadge}
          />
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            {tickets.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Aucune demande de support</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <Card 
                    key={ticket.id} 
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => setSelectedTicket(ticket)}
                    data-testid={`ticket-item-${ticket.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          {getSubjectIcon(ticket.subject)}
                          <div>
                            <p className="font-medium text-sm">
                              {getSubjectLabel(ticket.subject)}
                              {ticket.reference_id && (
                                <span className="text-muted-foreground ml-2">
                                  #{ticket.reference_id.slice(0, 8)}
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {ticket.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(ticket.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {getStatusBadge(ticket.status)}
                          {ticket.replies?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {ticket.replies.length} réponse{ticket.replies.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Sub-component for ticket detail view
const TicketDetail = ({ ticket, onReply, onBack, formatDate, getSubjectLabel, getStatusBadge }) => {
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendReply = async () => {
    if (!replyMessage.trim() || ticket.status === 'closed') return;
    setSending(true);
    await onReply(ticket.id, replyMessage);
    setReplyMessage('');
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
        ← Retour aux demandes
      </Button>

      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">{getSubjectLabel(ticket.subject)}</h4>
          {ticket.reference_id && (
            <p className="text-sm text-muted-foreground">Réf: {ticket.reference_id}</p>
          )}
        </div>
        {getStatusBadge(ticket.status)}
      </div>

      <ScrollArea className="h-[250px] border rounded-lg p-4">
        <div className="space-y-4">
          {/* Original message */}
          <div className="flex justify-end">
            <div className="max-w-[80%] bg-primary/10 rounded-lg p-3">
              <p className="text-sm">{ticket.message}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatDate(ticket.created_at)}</p>
            </div>
          </div>

          {/* Replies */}
          {ticket.replies?.map((reply, index) => (
            <div 
              key={index} 
              className={`flex ${reply.sender_role === 'admin' ? 'justify-start' : 'justify-end'}`}
            >
              <div className={`max-w-[80%] rounded-lg p-3 ${
                reply.sender_role === 'admin' 
                  ? 'bg-primary/10 border border-primary/20' 
                  : 'bg-muted'
              }`}>
                {reply.sender_role === 'admin' && (
                  <p className="text-xs font-medium text-primary mb-1">
                    Support Kacos
                  </p>
                )}
                <p className="text-sm">{reply.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatDate(reply.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {ticket.status !== 'closed' ? (
        <div className="flex gap-2">
          <Textarea
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            placeholder="Votre réponse..."
            rows={2}
            className="flex-1"
            data-testid="ticket-reply-input"
          />
          <Button 
            onClick={handleSendReply} 
            disabled={sending || !replyMessage.trim()}
            data-testid="ticket-reply-btn"
          >
            {sending ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      ) : (
        <div className="text-center py-2 text-sm text-muted-foreground bg-muted/50 rounded-lg">
          <CheckCircle className="h-4 w-4 inline mr-2" />
          Ce ticket est résolu
        </div>
      )}
    </div>
  );
};

export default SupportContact;
